import type { PopperPlacementType } from '@mui/material';
import { MenuItem, MenuList, Paper, Popper, Typography } from '@mui/material';
import type { SuggestionProps } from '@tiptap/suggestion';
import { translateBlock } from 'i18n';
import type { DataCatalogField } from 'modules/datacatalog/types';
import {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useMemo,
    useState,
} from 'react';

import type { FieldListActions } from './types';

type FieldListProps = {
    items: DataCatalogField[];
    command: ({ id }: { id: string }) => void;
    clientRect: (() => DOMRect) | null;
} & SuggestionProps;

const FieldList = forwardRef<FieldListActions, FieldListProps>((props, ref) => {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const { clientRect, command, items } = props;

    const referenceEl = useMemo(
        () => (clientRect ? { getBoundingClientRect: clientRect } : null),
        [clientRect],
    );

    const selectItem = (index: number) => {
        const item = items[index];
        if (item) {
            command({ id: item.field });
        }
    };

    const handleArrowUp = () => {
        if (items.length === 0) return;
        setSelectedIndex((prev) => (prev + items.length - 1) % items.length);
    };

    const handleArrowDown = () => {
        if (items.length === 0) return;
        setSelectedIndex((prev) => (prev + 1) % items.length);
    };

    // Reset selected index when the text search changes
    useEffect(() => setSelectedIndex(0), [items]);

    useImperativeHandle(ref, () => ({
        onKeyDown: ({ event }: { event: KeyboardEvent }) => {
            if (event.key === 'ArrowUp') {
                handleArrowUp();
                return true;
            }
            if (event.key === 'ArrowDown') {
                handleArrowDown();
                return true;
            }
            if (event.key === 'Enter') {
                selectItem(selectedIndex);
                return true;
            }
            return false;
        },
    }));

    if (referenceEl === null) {
        return <></>;
    }

    return (
        <Popper
            open={Boolean(referenceEl)}
            sx={{ zIndex: 'tooltip' }}
            anchorEl={referenceEl}
            placement="right-start"
            modifiers={[
                {
                    name: 'offset',
                    options: {
                        // Offset the popper vertically by the reference element's height
                        // so the suggestion list appears below the trigger character,
                        // and horizontally by 0 (aligned to the right edge of the reference).
                        offset: (args: {
                            popper: DOMRect;
                            reference: DOMRect;
                            placement: PopperPlacementType;
                        }): [number, number] => [args.reference.height, 0],
                    },
                },
            ]}
        >
            <Paper>
                <MenuList>
                    {items.map((item, index) => (
                        <MenuItem
                            key={item.field}
                            selected={index === selectedIndex}
                            onClick={() => selectItem(index)}
                        >
                            <Typography variant="body2">
                                {translateBlock(item.field)}
                            </Typography>
                            <Typography
                                variant="caption"
                                color="textSecondary"
                                sx={{ ml: 1 }}
                            >
                                {translateBlock(item.table)}
                            </Typography>
                        </MenuItem>
                    ))}
                </MenuList>
            </Paper>
        </Popper>
    );
});

FieldList.displayName = 'FieldList';
export default FieldList;
